/*
 * /shooting-targets — мишени для печати, разбор пробоин по снимку, подбор дроби.
 * Геометрия — targets.js, зрение — cv.js, числа — analysis.js, дробь — shot.js,
 * баллистика шара — ../ballistic-calculator/engine.js.
 */
import { zeroTarget, ringTarget, patternSheet, markerCards, cardsSheet, tiles, toSvg, inkMask, markCenters, gridStepMm } from './targets.js';
import { grayFromRGBA, downscale, findMarks, homography, rectify, findHoles } from './cv.js';
import { groupStats, patternStats } from './analysis.js';
import { SYSTEMS, GAME, gameRangeMm, sizesInRange, pelletsInLoad, shotTable } from './shot.js';
import { U } from '../ballistic-calculator/engine.js';

const CFG = window.__ST || { T: {}, cfg: { locale: 'en', slug: 'shooting-targets' } };
const T = CFG.T;
const SLUG = CFG.cfg.slug;
const LOCALE = CFG.cfg.locale;
const t = (k, r) => {
    let s = T[k] || k;
    if (r) for (const [a, b] of Object.entries(r)) s = s.split(':' + a).join(b);
    return s;
};
const $ = (id) => document.getElementById(id);
const NFL = window.__intlLocale ? window.__intlLocale() : LOCALE;
const fmt = (v, d = 0) => (v == null || !isFinite(v) ? '—' : new Intl.NumberFormat(NFL, { maximumFractionDigits: d, minimumFractionDigits: d }).format(v));
const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const PR = new Intl.PluralRules(NFL);
const yieldTick = () => (window.__yieldTick ? window.__yieldTick(0) : new Promise((r) => setTimeout(r, 0)));

const IMPERIAL = LOCALE === 'en';
const S = {
    tab: 'print',
    type: 'zero1', paper: IMPERIAL ? 'letter' : 'a4', dist: IMPERIAL ? 91.44 : 100, distU: IMPERIAL ? 'yd' : 'm',
    grid: IMPERIAL ? 'moa' : 'mil', sheet: 'cards',
    atype: 'zero1', plainW: 1000, plainH: 1000, spanW: 800, spanH: 800, adist: IMPERIAL ? 91.44 : 100, adistU: IMPERIAL ? 'yd' : 'm',
    mode: 'rifle', cal: 7.8, click: IMPERIAL ? 'moa:0.25' : 'mil:0.1',
    shotSys: IMPERIAL ? 'us' : (LOCALE === 'ru' || LOCALE === 'uk' ? 'ru' : 'eu'), shotIdx: 7, shotMat: 'lead', load: 32,
    circle: IMPERIAL ? 762 : 750, light: false,
    game: 'duck_near', sdist: 30, sload: 32, sv0: 400,
};
function load() {
    try {
        const s = window.__toolSettings?.load(SLUG, 'state', null);
        if (s && typeof s === 'object') Object.assign(S, s, { tab: 'print' });
    } catch { /* приватный режим */ }
}
let saveT = 0;
function save() {
    clearTimeout(saveT);
    saveT = setTimeout(() => { try { window.__toolSettings?.save(SLUG, 'state', { ...S }); } catch { /* noop */ } }, 400);
}
const num = (el) => { const v = parseFloat(String(el.value).replace(',', '.')); return isFinite(v) ? v : NaN; };
const DISTK = { m: 1, yd: U.M_PER_YD };

/* ── вкладки ─────────────────────────────────────────────────────────────── */
function setTab(tab) {
    S.tab = tab;
    document.querySelectorAll('[data-sttab]').forEach((b) => {
        const on = b.dataset.sttab === tab;
        b.classList.toggle('on', on);
        b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    document.querySelectorAll('[data-stpane]').forEach((p) => { p.hidden = p.dataset.stpane !== tab; });
    if (tab === 'shot') renderShot();
    if (tab === 'analyze' && photo) drawWork();
}

/* ── мишень ──────────────────────────────────────────────────────────────── */
function gridLabel(unit, distM) {
    const step = gridStepMm(unit, distM);
    const name = { moa: '1 MOA', mil: '0,1 MIL', cm: '1 cm', in: '1″' }[unit];
    return { step, name: unit === 'mil' ? `${fmt(0.1, 1)} MIL` : name };
}

function buildTarget(type, opts = {}) {
    const paper = opts.paper || S.paper;
    const distM = opts.distM || S.dist;
    const unit = opts.grid || S.grid;
    const g = gridLabel(unit, distM);
    const distTxt = `${fmt(distM / DISTK[S.distU], 0)} ${t(S.distU === 'yd' ? 'u_yd' : 'u_m')}`;
    const labels = {
        title: type === 'rings' ? t('lbl_title_rings', { d: distTxt }) : (type === 'pattern' ? t('lbl_title_pattern') : t('lbl_title_zero', { d: distTxt })),
        sub: type === 'pattern' || type === 'rings' ? '' : t('lbl_sub_grid', { g: g.name, mm: fmt(g.step, 1) }),
        scale: t('lbl_scale'),
        brand: CFG.cfg.brand || '',
    };
    if (type === 'rings') return ringTarget({ paper, distanceM: distM, labels });
    if (type === 'pattern' && (opts.sheet || S.sheet) === 'cards') {
        return markerCards({ paper, labels: {
            title: t('lbl_title_cards'), tl: t('card_tl'), tr: t('card_tr'), bl: t('card_bl'), br: t('card_br'),
            aim: t('card_aim'), how: t('cards_how'), scale: t('lbl_scale'),
        } });
    }
    if (type === 'pattern') return patternSheet({ sheetMm: 1000, distanceM: distM, labels });
    return zeroTarget({ paper, unit, distanceM: distM, aims: type === 'zero5' ? 5 : 1, labels });
}

function renderPreview() {
    const tg = buildTarget(S.type);
    let svg = toSvg(tg, { label: t('lbl_title_zero', { d: '' }) });
    if (S.type === 'pattern' && S.sheet === 'tiles') {
        const lay = tiles(tg, S.paper);
        const lines = lay.pages.map((p) => `<rect x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" fill="none" stroke="#2563eb" stroke-width="2" stroke-dasharray="10 8"/>`).join('');
        svg = svg.replace('</svg>', lines + '</svg>');
    }
    $('stPreview').innerHTML = svg;
    const g = gridLabel(S.grid, S.dist);
    let info;
    if (S.type === 'pattern' && S.sheet === 'cards') {
        info = t('print_cards');
    } else if (S.type === 'pattern') {
        const lay = tiles(tg, S.paper);
        info = t('grid_info_pattern') + (S.sheet === 'tiles' ? ' ' + t('print_tiles', { n: lay.pages.length, r: lay.rows, c: lay.cols }) : '');
    } else if (S.type === 'rings') {
        info = t('grid_info_rings');
    } else {
        info = t('grid_info', { g: g.name, d: `${fmt(S.dist / DISTK[S.distU], 0)} ${t(S.distU === 'yd' ? 'u_yd' : 'u_m')}`, mm: fmt(g.step, 1) });
    }
    $('stGridInfo').textContent = info;
    const isPattern = S.type === 'pattern';
    $('stSheet').closest('.tc-f').hidden = !isPattern;
    $('stGrid').closest('.tc-f').hidden = isPattern || S.type === 'rings';
}

/* ── PDF ─────────────────────────────────────────────────────────────────── */
async function ensurePdfLibs() {
    if (typeof window.__vendorScript !== 'function') throw new Error('no_vendor_loader');
    await window.__vendorScript(CFG.cfg.pdfLib, { expect: 'PDFLib', label: 'pdf-lib' });
    await window.__vendorScript(CFG.cfg.pdfFont, { expect: 'PdfTextFont', label: 'pdf-text-font' });
}

async function makePdf() {
    const btn = $('stPdf');
    btn.disabled = true;
    try {
        await ensurePdfLibs();
        const tg = buildTarget(S.type);
        const PL = window.PDFLib, PTF = window.PdfTextFont;
        const doc = await PL.PDFDocument.create();
        const PT = 72 / 25.4;
        const black = PL.rgb(0, 0, 0), white = PL.rgb(1, 1, 1), blue = PL.rgb(0.15, 0.39, 0.92);
        const allText = tg.shapes.filter((s) => s.t === 'text').map((s) => s.str).join(' ');
        await PTF.ready(allText).catch(() => PTF.load());
        const fonts = {};
        const fontOf = async (id) => { const k = id || 'noto'; if (!fonts[k]) fonts[k] = (await PTF.face(k)).font; return fonts[k]; };
        let pages;
        if (S.type === 'pattern' && S.sheet === 'tiles') {
            const lay = tiles(tg, S.paper);
            pages = lay.pages.map((p) => ({ ...p, pw: lay.paperW, ph: lay.paperH, label: t('lbl_tile', { r: p.row + 1, c: p.col + 1, n: lay.rows * lay.cols }) }));
        } else {
            pages = [{ x: 0, y: 0, w: tg.w, h: tg.h, margin: 0, pw: tg.w, ph: tg.h }];
        }
        for (const p of pages) {
            const page = doc.addPage([p.pw * PT, p.ph * PT]);
            const X = (x) => (x - p.x + p.margin) * PT;
            const Y = (y) => (p.ph - (y - p.y + p.margin)) * PT;
            const inPage = (x0, y0, x1, y1) => !(x1 < p.x - 2 || x0 > p.x + p.w + 2 || y1 < p.y - 2 || y0 > p.y + p.h + 2);
            for (const s of tg.shapes) {
                if (s.t === 'line') {
                    if (!inPage(Math.min(s.x1, s.x2), Math.min(s.y1, s.y2), Math.max(s.x1, s.x2), Math.max(s.y1, s.y2))) continue;
                    page.drawLine({ start: { x: X(s.x1), y: Y(s.y1) }, end: { x: X(s.x2), y: Y(s.y2) }, thickness: s.w * PT, color: black, dashArray: s.dash ? [4 * PT, 3 * PT] : undefined });
                } else if (s.t === 'circle') {
                    if (!inPage(s.cx - s.r, s.cy - s.r, s.cx + s.r, s.cy + s.r)) continue;
                    page.drawEllipse({ x: X(s.cx), y: Y(s.cy), xScale: s.r * PT, yScale: s.r * PT, borderWidth: s.w * PT, borderColor: black, borderDashArray: s.dash ? [6 * PT, 4 * PT] : undefined });
                } else if (s.t === 'rect') {
                    if (!inPage(s.x, s.y, s.x + s.w, s.y + s.h)) continue;
                    page.drawRectangle({ x: X(s.x), y: Y(s.y + s.h), width: s.w * PT, height: s.h * PT, color: s.fill ? black : white });
                } else if (s.t === 'text') {
                    if (!inPage(s.x - 80, s.y - s.size, s.x + 80, s.y)) continue;
                    await drawText(page, PTF, fontOf, s.str, X(s.x), Y(s.y), s.size * PT, s.anchor, black);
                }
            }
            if (p.label) {
                // метки совмещения по углам рабочей области страницы
                for (const [cx, cy] of [[p.x, p.y], [p.x + p.w, p.y], [p.x, p.y + p.h], [p.x + p.w, p.y + p.h]]) {
                    page.drawLine({ start: { x: X(cx - 4), y: Y(cy) }, end: { x: X(cx + 4), y: Y(cy) }, thickness: 0.3 * PT, color: blue });
                    page.drawLine({ start: { x: X(cx), y: Y(cy - 4) }, end: { x: X(cx), y: Y(cy + 4) }, thickness: 0.3 * PT, color: blue });
                }
                await drawText(page, PTF, fontOf, p.label, X(p.x + 3), Y(p.y + 6), 2.8 * PT, 'start', blue);
            }
        }
        const bytes = await doc.save();
        const ok = await window.__downloadFile?.(new Blob([bytes], { type: 'application/pdf' }), `${t('pdf_name')}-${S.type}.pdf`);
        if (ok === false) window.__toast?.(t('pdf_fail'), 'error');
    } catch (e) {
        window.__handleError?.(e, 'targets_pdf', { say: t('pdf_fail') });
    } finally {
        btn.disabled = false;
    }
}

async function drawText(page, PTF, fontOf, str, x, yBase, sizePt, anchor, color) {
    const runs = PTF.visualRuns(String(str), 'noto');
    const parts = [];
    let width = 0;
    for (const r of runs) {
        const font = await fontOf(r.faceId || 'noto');
        const o = PTF.outlineWith(font, r.text, sizePt);
        parts.push({ d: o.path, w: o.width });
        width += o.width;
    }
    let x0 = anchor === 'middle' ? x - width / 2 : (anchor === 'end' ? x - width : x);
    for (const p of parts) {
        if (p.d) window.__pdfSvgPath(page, p.d, { x: x0, y: yBase, color, borderWidth: 0 });
        x0 += p.w;
    }
}

/* ── разбор по фото ──────────────────────────────────────────────────────── */
let photo = null;          // {w,h,d} — полный снимок в полутонах
let photoRGB = null;       // уменьшенная цветная копия для ручных углов
let work = null;           // { target, ppm, rect, holes, aim, manual }
let corners = null;        // ручные углы в пикселях снимка
let tapMode = 'hole';

const CALS = [
    [5.6, '.22 LR / .223 Rem'], [6.5, '6.5 mm'], [7.0, '.270 / 7 mm'], [7.8, '.308 / .30-06 / 7.62'],
    [8.2, '8×57 JS'], [9.3, '9.3×62 / 9.3×74R'], [9.6, '.366 TKM'], [11.6, '.45-70'], [18.5, 'slug'], [4.5, 'air'],
];
function fillCalibers() {
    const sel = $('stCal');
    for (const [mm, name] of CALS) {
        const o = document.createElement('option');
        o.value = String(mm);
        const label = name === 'slug' ? t('cal_slug12') : (name === 'air' ? t('cal_air') : name);
        o.textContent = `${label} (${fmt(mm, 1)} ${t('u_mm')})`;
        sel.appendChild(o);
    }
}

function shotLabel(sys, n, d) {
    const pre = sys === 'ru' ? '№ ' : '#';
    return `${pre}${n} — ${fmt(d, 2)} ${t('u_mm')}`;
}
function fillShotNumbers() {
    const sel = $('stShotNo');
    sel.innerHTML = '';
    SYSTEMS[S.shotSys].forEach(([n, d], i) => {
        const o = document.createElement('option');
        o.value = String(i);
        o.textContent = shotLabel(S.shotSys, n, d);
        sel.appendChild(o);
    });
    if (S.shotIdx >= SYSTEMS[S.shotSys].length) S.shotIdx = 7;
    sel.value = String(S.shotIdx);
}

function targetForAnalysis() {
    if (S.atype === 'plain') return { w: S.plainW, h: S.plainH, shapes: [], aims: [[S.plainW / 2, S.plainH / 2]], kind: 'plain' };
    if (S.atype === 'cards') return cardsSheet(S.spanW, S.spanH);
    return buildTarget(S.atype, { distM: S.adist, sheet: 'plot' });
}

async function onPhoto(file) {
    setStatus(t('st_reading'));
    $('stWork').hidden = false;
    $('stHowShoot').hidden = true;
    try {
        const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const max = 3200;
        const k = Math.min(1, max / Math.max(bmp.width, bmp.height));
        const w = Math.round(bmp.width * k), h = Math.round(bmp.height * k);
        const cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        const ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(bmp, 0, 0, w, h);
        bmp.close?.();
        photo = grayFromRGBA(ctx.getImageData(0, 0, w, h).data, w, h);
        photoRGB = cv;
        corners = null;
        await yieldTick();
        await analyze();
    } catch (e) {
        window.__handleError?.(e, 'targets_photo', { say: t('img_fail') });
        setStatus(t('img_fail'));
    }
}

function setStatus(s) { $('stStatus').textContent = s; }
/* Оружие следует за мишенью: осыпь — ружьё, пристрелочная и кольца — нарезное (пулю 12 к. выбирают калибром);
   у листа без меток решает человек. Иначе снимок пристрелочной мишени после печати осыпи считался дробью. */
function modeFor(atype) { return atype === 'pattern' || atype === 'cards' ? 'shotgun' : (atype === 'plain' ? null : 'rifle'); }
/* Воронка: сколько снимков нашли метки сами, сколько ушли в ручные углы и правки касанием. */
function stage(name, meta) { try { window.__stage?.(name, meta); } catch (_) { /* телеметрия не мешает разбору */ } }

async function analyze() {
    if (!photo) return;
    const tg = targetForAnalysis();
    let H = null;
    if (corners) {
        H = homography([[0, 0], [tg.w, 0], [tg.w, tg.h], [0, tg.h]], corners);
        stage('corners_manual', { atype: S.atype });
    } else if (S.atype !== 'plain') {
        const { img, scale } = downscale(photo, 1600);
        await yieldTick();
        const marks = findMarks(img);
        stage(marks ? 'marks_found' : 'marks_missing', { atype: S.atype });
        if (marks) {
            const mc = markCenters(tg.w, tg.h);
            H = homography([mc.tl, mc.tr, mc.bl, mc.br], [marks.tl, marks.tr, marks.bl, marks.br].map(([x, y]) => [(x + 0.5) * scale - 0.5, (y + 0.5) * scale - 0.5]));
        }
    }
    if (!H) {
        startManual(S.atype === 'plain' ? t('st_manual') : t('st_marks_fail'));
        return;
    }
    const ppm = tg.w > 600 ? 2 : 4;
    const rect = rectify(photo, H, tg.w, tg.h, ppm);
    await yieldTick();
    const mask = tg.shapes.length ? inkMask(tg, ppm) : null;
    const holeMm = S.mode === 'shotgun' ? SYSTEMS[S.shotSys][S.shotIdx][1] : S.cal;
    const res = findHoles(rect, ppm, { holeMm, mask, polarity: S.light ? 'light' : 'dark' });
    work = { target: tg, ppm, rect, holes: res.holes.map((h) => ({ x: h.x, y: h.y })), aim: null, holeMm };
    stopManual();
    setStatus((corners ? '' : t('st_marks_ok') + ' ') + (work.holes.length ? t('st_found', { n: work.holes.length }) : t('st_none')) + ' ' + t('st_tap'));
    drawWork();
    renderStats();
    if (!completed && work.holes.length) { completed = true; window.__aeToolComplete?.({ kind: S.mode, holes: work.holes.length }); }
}
let completed = false;

/* ручные углы */
function startManual(msg) {
    work = null;
    $('stResults').hidden = true;
    setStatus(msg);
    const cv = $('stCanvas');
    cv.width = photoRGB.width; cv.height = photoRGB.height;
    cv.getContext('2d').drawImage(photoRGB, 0, 0);
    const k = photo.w / photoRGB.width;
    if (!corners) {
        corners = [[0.12, 0.1], [0.88, 0.1], [0.88, 0.9], [0.12, 0.9]].map(([a, b]) => [a * photo.w, b * photo.h]);
    }
    placeHandles(k);
    $('stManualBar').hidden = false;
    document.querySelectorAll('.st-handle').forEach((h) => { h.hidden = false; });
}
function stopManual() {
    $('stManualBar').hidden = true;
    document.querySelectorAll('.st-handle').forEach((h) => { h.hidden = true; });
}
function placeHandles() {
    const cv = $('stCanvas');
    const r = cv.getBoundingClientRect();
    const wr = $('stCanvasWrap').getBoundingClientRect();
    document.querySelectorAll('.st-handle').forEach((h) => {
        const i = +h.dataset.corner;
        const [x, y] = corners[i];
        h.style.left = (r.left - wr.left + x / photo.w * r.width) + 'px';
        h.style.top = (r.top - wr.top + y / photo.h * r.height) + 'px';
    });
}
function bindHandles() {
    document.querySelectorAll('.st-handle').forEach((h) => {
        const i = +h.dataset.corner;
        h.addEventListener('pointerdown', (e) => {
            e.preventDefault();
            h.setPointerCapture(e.pointerId);
            const move = (ev) => {
                const r = $('stCanvas').getBoundingClientRect();
                const x = Math.max(0, Math.min(r.width, ev.clientX - r.left)), y = Math.max(0, Math.min(r.height, ev.clientY - r.top));
                corners[i] = [x / r.width * photo.w, y / r.height * photo.h];
                placeHandles();
            };
            const up = () => { h.removeEventListener('pointermove', move); h.removeEventListener('pointerup', up); h.removeEventListener('pointercancel', up); };
            h.addEventListener('pointermove', move);
            h.addEventListener('pointerup', up);
            h.addEventListener('pointercancel', up);
        });
        h.addEventListener('keydown', (e) => {
            const step = e.shiftKey ? 20 : 4;
            const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
            if (!d) return;
            e.preventDefault();
            corners[i] = [corners[i][0] + d[0], corners[i][1] + d[1]];
            placeHandles();
        });
    });
}

/* рисунок разбора */
function drawWork() {
    if (!work) return;
    const { rect, target, ppm } = work;
    const cv = $('stCanvas');
    cv.width = rect.w; cv.height = rect.h;
    const ctx = cv.getContext('2d');
    const id = ctx.createImageData(rect.w, rect.h);
    for (let i = 0, j = 0; i < rect.d.length; i++, j += 4) { const v = rect.d[i]; id.data[j] = v; id.data[j + 1] = v; id.data[j + 2] = v; id.data[j + 3] = 255; }
    ctx.putImageData(id, 0, 0);
    const P = (mm) => mm * ppm;
    const hr = Math.max(3, work.holeMm * 0.5 * ppm);
    ctx.lineWidth = Math.max(2, ppm * 0.6);
    if (S.mode === 'shotgun') {
        const st = statsNow();
        if (st && st.pattern) {
            const c = st.pattern.center;
            ctx.strokeStyle = 'rgba(37,99,235,.9)';
            ctx.beginPath(); ctx.arc(P(c[0]), P(c[1]), P(S.circle / 2), 0, Math.PI * 2); ctx.stroke();
            ctx.setLineDash([P(8), P(6)]);
            ctx.beginPath(); ctx.arc(P(c[0]), P(c[1]), P(S.circle / 4), 0, Math.PI * 2); ctx.stroke();
            ctx.setLineDash([]);
        }
    }
    ctx.strokeStyle = 'rgba(22,163,74,.95)';
    for (const h of work.holes) { ctx.beginPath(); ctx.arc(P(h.x), P(h.y), hr, 0, Math.PI * 2); ctx.stroke(); }
    const aims = work.aim ? [work.aim] : target.aims;
    ctx.strokeStyle = 'rgba(245,158,11,1)';
    for (const a of aims) cross(ctx, P(a[0]), P(a[1]), hr * 1.6);
    if (S.mode === 'rifle') {
        const st = statsNow();
        for (const g of (st && st.groups) || []) {
            ctx.strokeStyle = 'rgba(220,38,38,1)';
            cross(ctx, P(g.mpi[0]), P(g.mpi[1]), hr * 1.3, true);
            if (g.ends) {
                ctx.setLineDash([P(2), P(2)]);
                ctx.beginPath(); ctx.moveTo(P(g.ends[0].x), P(g.ends[0].y)); ctx.lineTo(P(g.ends[1].x), P(g.ends[1].y)); ctx.stroke();
                ctx.setLineDash([]);
            }
        }
    }
}
function cross(ctx, x, y, r, diag) {
    ctx.beginPath();
    if (diag) { ctx.moveTo(x - r, y - r); ctx.lineTo(x + r, y + r); ctx.moveTo(x + r, y - r); ctx.lineTo(x - r, y + r); }
    else { ctx.moveTo(x - r, y); ctx.lineTo(x + r, y); ctx.moveTo(x, y - r); ctx.lineTo(x, y + r); }
    ctx.stroke();
}

function clickRad() {
    const [k, v] = S.click.split(':');
    return parseFloat(v) * (k === 'moa' ? U.RAD_PER_MOA : U.RAD_PER_MIL);
}

function statsNow() {
    if (!work) return null;
    if (S.mode === 'shotgun') {
        const d = SYSTEMS[S.shotSys][S.shotIdx][1];
        const total = pelletsInLoad(S.load, d, S.shotMat);
        return { pattern: patternStats(work.holes, { circleMm: S.circle, innerMm: S.circle / 2, total, aim: work.aim || work.target.aims[0], sheetW: work.target.w, sheetH: work.target.h }) };
    }
    const aims = work.aim ? [work.aim] : work.target.aims;
    const by = aims.map(() => []);
    for (const h of work.holes) {
        let bi = 0, bd = Infinity;
        aims.forEach((a, i) => { const d = Math.hypot(h.x - a[0], h.y - a[1]); if (d < bd) { bd = d; bi = i; } });
        by[bi].push(h);
    }
    const groups = [];
    by.forEach((hs, i) => { if (hs.length) groups.push({ ...groupStats(hs, aims[i], S.adist, clickRad()), aimIndex: i }); });
    return { groups };
}

function clickTxt(n) {
    const w = T['r_clicks_' + PR.select(Math.abs(n))] || T.r_clicks_other || '';
    return `${fmt(Math.abs(n), 0)} ${w}`;
}

function renderStats() {
    const st = statsNow();
    const box = $('stStats');
    if (!st) { $('stResults').hidden = true; return; }
    $('stResults').hidden = false;
    const ang = S.click.startsWith('moa') ? 'MOA' : 'MIL';
    const angOf = (g, key) => (ang === 'MOA' ? g[key + 'Moa'] : g[key + 'Mil']);
    if (S.mode === 'rifle') {
        if (!st.groups.length) { box.innerHTML = `<p class="tc-msg">${esc(t('st_none'))}</p>`; return; }
        box.innerHTML = st.groups.map((g, gi) => {
            const v = g.upClicks, h = g.rightClicks;
            const fixV = v === 0 ? '' : `${esc(t(v > 0 ? 'r_up' : 'r_down'))} ${esc(clickTxt(v))}`;
            const fixH = h === 0 ? '' : `${esc(t(h > 0 ? 'r_right' : 'r_left'))} ${esc(clickTxt(h))}`;
            const fix = [fixV, fixH].filter(Boolean).join(LOCALE === 'ar' ? '، ' : ', ') || esc(t('r_center'));
            return `<div class="tc-answer"${gi ? ' style="margin-top:12px"' : ''}>
                <span class="tc-answer-l">${st.groups.length > 1 ? esc(t('r_group', { n: g.aimIndex + 1 })) + ' · ' : ''}${esc(t('r_fix'))}</span>
                <span class="tc-answer-v" style="font-size:clamp(22px,5cqi,34px)">${fix}</span>
            </div>
            <div class="tc-stats" style="margin-top:8px">
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('r_holes'))}</div><div class="tc-stat-v">${g.n}</div></div>
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('r_es'))}</div><div class="tc-stat-v">${fmt(g.esMm, 0)} ${esc(t('u_mm'))}</div><div class="tc-stat-s">${fmt(angOf(g, 'es'), 2)} ${ang}</div></div>
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('r_mr'))}</div><div class="tc-stat-v">${fmt(g.mrMm, 0)} ${esc(t('u_mm'))}</div><div class="tc-stat-s">${fmt(angOf(g, 'mr'), 2)} ${ang}</div></div>
                <div class="tc-stat"><div class="tc-stat-l">${esc(t('r_mpi'))}</div><div class="tc-stat-v">${fmt(Math.abs(g.dyMm) / 10, 1)} / ${fmt(Math.abs(g.dxMm) / 10, 1)}</div><div class="tc-stat-s">${esc(t(g.dyMm >= 0 ? 'r_up' : 'r_down'))} / ${esc(t(g.dxMm >= 0 ? 'r_right' : 'r_left'))}, ${esc(t('u_cm'))}</div></div>
            </div>`;
        }).join('');
        return;
    }
    const p = st.pattern;
    const chokeKey = p.pct == null ? null : (p.pct < 45 ? 'choke_cyl' : p.pct < 55 ? 'choke_ic' : p.pct < 62.5 ? 'choke_mod' : p.pct < 67.5 ? 'choke_im' : p.pct <= 72.5 ? 'choke_full' : 'choke_over');
    box.innerHTML = `<div class="tc-answer">
            <span class="tc-answer-l">${esc(t('p_pct'))}</span>
            <span class="tc-answer-v">${p.pct == null ? '—' : fmt(p.pct, 0) + ' %'}</span>
            <p class="tc-answer-sub">${esc(t('p_in', { n: p.inCircle, total: p.total }))}${chokeKey ? ' · ' + esc(t('p_choke', { c: t(chokeKey) })) : ''}</p>
        </div>
        <div class="tc-stats" style="margin-top:8px">
            <div class="tc-stat"><div class="tc-stat-l">${esc(t('p_inner'))}</div><div class="tc-stat-v">${fmt(p.innerDensity, 1)}</div><div class="tc-stat-s">${esc(t('u_dm2'))}</div></div>
            <div class="tc-stat"><div class="tc-stat-l">${esc(t('p_ring'))}</div><div class="tc-stat-v">${fmt(p.ringDensity, 1)}</div><div class="tc-stat-s">${esc(t('u_dm2'))}</div></div>
            <div class="tc-stat"><div class="tc-stat-l">${esc(t('p_even'))}</div><div class="tc-stat-v">${p.evenness == null ? '—' : fmt(p.evenness, 2)}</div></div>
            <div class="tc-stat"><div class="tc-stat-l">${esc(t('p_gap'))}</div><div class="tc-stat-v">${fmt(p.biggestGapMm / 10, 0)} ${esc(t('u_cm'))}</div></div>
            <div class="tc-stat"><div class="tc-stat-l">${esc(t('p_center'))}</div><div class="tc-stat-v">${p.offset ? `${fmt(Math.abs(p.offset[1]) / 10, 0)} / ${fmt(Math.abs(p.offset[0]) / 10, 0)}` : '—'}</div><div class="tc-stat-s">${p.offset ? `${esc(t(p.offset[1] >= 0 ? 'r_up' : 'r_down'))} / ${esc(t(p.offset[0] >= 0 ? 'r_right' : 'r_left'))}, ${esc(t('u_cm'))}` : ''}</div></div>
        </div>`;
}

function onCanvasTap(e) {
    if (!work) return;
    const r = $('stCanvas').getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width * work.target.w;
    const y = (e.clientY - r.top) / r.height * work.target.h;
    if (tapMode === 'aim') {
        work.aim = [x, y];
    } else {
        const lim = Math.max(3, work.holeMm * 0.7);
        let bi = -1, bd = Infinity;
        work.holes.forEach((h, i) => { const d = Math.hypot(h.x - x, h.y - y); if (d < bd) { bd = d; bi = i; } });
        if (bi >= 0 && bd <= lim) work.holes.splice(bi, 1);
        else work.holes.push({ x, y });
        if (!work.edited) { work.edited = true; stage('tap_edit', { mode: S.mode }); }
    }
    drawWork();
    renderStats();
}

async function savePng() {
    try {
        const cv = $('stCanvas');
        const blob = await window.__canvasBlob(cv, 'image/png');
        await window.__downloadFile?.(blob, `${t('png_name')}.png`);
    } catch (e) {
        window.__handleError?.(e, 'targets_png', { say: t('img_fail') });
    }
}

/* ── дробь ───────────────────────────────────────────────────────────────── */
function fillGames() {
    const sel = $('shGame');
    for (const g of GAME) {
        const o = document.createElement('option');
        o.value = g.id;
        o.textContent = t('game_' + g.id);
        sel.appendChild(o);
    }
}

function renderShot() {
    const sys = S.shotSys;
    const range = gameRangeMm(S.game, S.shotMat);
    const rec = sizesInRange(sys, range);
    const pre = sys === 'ru' ? '№ ' : '#';
    const recTxt = rec.length ? `${pre}${rec[rec.length - 1][0]}–${rec[0][0]}`.replace(/^(.+)–\1$/, '$1') : '—';
    const dmin = rec.length ? rec[0][1] : range[0], dmax = rec.length ? rec[rec.length - 1][1] : range[1];
    const mmTxt = dmin === dmax ? `${fmt(dmin, 2)} ${t('u_mm')}` : `${fmt(dmin, 2)}–${fmt(dmax, 2)} ${t('u_mm')}`;
    $('shAnswer').innerHTML = `<p class="st-rec">${esc(t('rec_text', { game: t('game_' + S.game), mat: t('mat_' + S.shotMat), nums: recTxt, mm: mmTxt }))}</p>`;
    const list = SYSTEMS[sys].filter(([, d]) => d >= range[0] - 0.8 && d <= range[1] + 0.8);
    const head = `<thead><tr><th>${esc(t('th_no'))}</th><th>${esc(t('th_mm'))}</th><th>${esc(t('th_count', { g: fmt(S.sload, 0) }))}</th><th>${esc(t('th_v', { d: fmt(S.sdist, 0) }))}</th><th>${esc(t('th_e', { d: fmt(S.sdist, 0) }))}</th><th>${esc(t('th_t'))}</th></tr></thead>`;
    const body = list.map(([n, d]) => {
        const tab = shotTable({ d, material: S.shotMat, loadG: S.sload, v0: S.sv0, ranges: [S.sdist] });
        const r = tab.rows[0] || {};
        const isRec = rec.some(([rn]) => rn === n);
        return `<tr${isRec ? ' class="st-row-rec"' : ''}><td>${esc(pre + n)}</td><td class="num">${fmt(d, 2)}</td><td class="num">${fmt(tab.count, 0)}</td><td class="num">${fmt(r.v, 0)}</td><td class="num">${fmt(r.e, 2)}</td><td class="num">${fmt(r.t, 3)}</td></tr>`;
    }).join('');
    $('shTable').innerHTML = head + `<tbody>${body}</tbody>`;
    $('shExplain').textContent = S.shotMat === 'steel' ? t('explain_steel') : t('explain_lead');
    renderCross();
    save();
}

function renderCross() {
    const rows = SYSTEMS.ru.map(([n, d]) => {
        const near = (sys) => {
            let best = null;
            for (const [m, dm] of SYSTEMS[sys]) if (Math.abs(dm - d) <= 0.13 && (!best || Math.abs(dm - d) < Math.abs(best[1] - d))) best = [m, dm];
            return best ? best[0] : '—';
        };
        return `<tr><td>№ ${esc(n)}</td><td>${fmt(d, 2)}</td><td>${esc(near('eu'))}</td><td>${esc(near('us'))}</td><td>${esc(near('uk'))}</td></tr>`;
    }).join('');
    $('shCross').innerHTML = `<thead><tr><th>${esc(t('cross_ru'))}</th><th>${esc(t('cross_mm'))}</th><th>${esc(t('cross_eu'))}</th><th>${esc(t('cross_us'))}</th><th>${esc(t('cross_uk'))}</th></tr></thead><tbody>${rows}</tbody>`;
}

/* ── связка ──────────────────────────────────────────────────────────────── */
function paintPrint() {
    $('stType').value = S.type; $('stPaper').value = S.paper; $('stGrid').value = S.grid; $('stSheet').value = S.sheet;
    $('stDistU').value = S.distU; $('stDist').value = String(Math.round(S.dist / DISTK[S.distU]));
}
function paintAnalyze() {
    $('stAType').value = S.atype; $('stADistU').value = S.adistU; $('stADist').value = String(Math.round(S.adist / DISTK[S.adistU]));
    $('stCal').value = String(S.cal); $('stClick').value = S.click; $('stShotMat').value = S.shotMat;
    $('stLoad').value = String(S.load); $('stCircle').value = String(S.circle); $('stLight').checked = !!S.light;
    const cards = S.atype === 'cards';
    $('stPlainW').value = String(cards ? S.spanW : S.plainW); $('stPlainH').value = String(cards ? S.spanH : S.plainH);
    $('stPlainWL').textContent = t(cards ? 'lbl_span_w' : 'lbl_plain_w');
    $('stPlainHL').textContent = t(cards ? 'lbl_span_h' : 'lbl_plain_h');
    $('stPlainSize').hidden = S.atype !== 'plain' && !cards;
    document.querySelectorAll('[data-stmode]').forEach((b) => { const on = b.dataset.stmode === S.mode; b.classList.toggle('on', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); });
    $('stRifle').hidden = S.mode !== 'rifle';
    $('stShotgun').hidden = S.mode !== 'shotgun';
    fillShotNumbers();
}
function paintShot() {
    $('shGame').value = S.game; $('shDist').value = String(S.sdist); $('shSystem').value = S.shotSys;
    $('shMat').value = S.shotMat; $('shLoad').value = String(S.sload); $('shV0').value = String(S.sv0);
}

function boot() {
    load();
    fillCalibers();
    fillGames();
    paintPrint(); paintAnalyze(); paintShot();
    renderPreview();
    document.querySelectorAll('[data-sttab]').forEach((b) => b.addEventListener('click', () => setTab(b.dataset.sttab)));
    for (const id of ['stType', 'stPaper', 'stGrid', 'stSheet', 'stDistU']) {
        $(id).addEventListener('change', () => {
            S.type = $('stType').value; S.paper = $('stPaper').value; S.grid = $('stGrid').value; S.sheet = $('stSheet').value;
            if (id === 'stDistU') S.distU = $('stDistU').value;
            if (id === 'stType' && S.type === 'pattern' && S.dist > 60) S.dist = IMPERIAL ? 36.58 : 35;
            if (id === 'stType' || id === 'stSheet') {
                S.atype = S.type === 'pattern' && S.sheet === 'cards' ? 'cards' : S.type;
                S.mode = modeFor(S.atype) || S.mode;
            }
            paintPrint(); paintAnalyze(); renderPreview(); save();
        });
    }
    $('stDist').addEventListener('input', () => { const v = num($('stDist')); if (v > 0) { S.dist = v * DISTK[S.distU]; S.adist = S.dist; renderPreview(); save(); } });
    $('stPdf').addEventListener('click', makePdf);

    const zone = $('stUpload');
    if (typeof window.__bindUploaderFile === 'function') window.__bindUploaderFile(zone, (f) => onPhoto(f));
    else zone.addEventListener('tool-uploader:file', (e) => { const f = e.detail && (e.detail.file || (e.detail.files || [])[0]); if (f) onPhoto(f); });
    $('stAType').addEventListener('change', () => { S.atype = $('stAType').value; S.mode = modeFor(S.atype) || S.mode; corners = null; paintAnalyze(); save(); if (photo) analyze(); });
    $('stADist').addEventListener('input', () => { const v = num($('stADist')); if (v > 0) { S.adist = v * DISTK[S.adistU]; renderStats(); save(); } });
    $('stADistU').addEventListener('change', () => { S.adistU = $('stADistU').value; paintAnalyze(); save(); });
    for (const id of ['stPlainW', 'stPlainH']) $(id).addEventListener('change', () => {
        const v = num($(id));
        if (!(v > 0)) return;
        const w = id === 'stPlainW';
        if (S.atype === 'cards') { if (w) S.spanW = v; else S.spanH = v; } else if (w) S.plainW = v; else S.plainH = v;
        save();
        if (photo) analyze();
    });
    document.querySelectorAll('[data-stmode]').forEach((b) => b.addEventListener('click', () => { S.mode = b.dataset.stmode; paintAnalyze(); save(); if (photo) analyze(); }));
    $('stCal').addEventListener('change', () => { S.cal = parseFloat($('stCal').value); save(); if (photo) analyze(); });
    $('stClick').addEventListener('change', () => { S.click = $('stClick').value; renderStats(); save(); });
    $('stShotNo').addEventListener('change', () => { S.shotIdx = parseInt($('stShotNo').value, 10) || 0; save(); if (photo) analyze(); });
    $('stShotMat').addEventListener('change', () => { S.shotMat = $('stShotMat').value; renderStats(); drawWork(); save(); });
    $('stLoad').addEventListener('input', () => { const v = num($('stLoad')); if (v > 0) { S.load = v; renderStats(); save(); } });
    $('stCircle').addEventListener('change', () => { S.circle = parseInt($('stCircle').value, 10); renderStats(); drawWork(); save(); });
    $('stLight').addEventListener('change', () => { S.light = $('stLight').checked; save(); if (photo) analyze(); });
    $('stRedo').addEventListener('click', () => { corners = null; if (photo) analyze(); });
    $('stCorners').addEventListener('click', () => { if (photo) startManual(t('st_manual')); });
    $('stApplyCorners').addEventListener('click', () => { analyze(); });
    $('stCanvas').addEventListener('click', onCanvasTap);
    document.querySelectorAll('[data-sttap]').forEach((b) => b.addEventListener('click', () => {
        tapMode = b.dataset.sttap;
        document.querySelectorAll('[data-sttap]').forEach((x) => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-checked', on ? 'true' : 'false'); });
        setStatus(t(tapMode === 'aim' ? 'st_tap_aim' : 'st_tap'));
    }));
    $('stSavePng').addEventListener('click', savePng);
    bindHandles();
    window.addEventListener('resize', () => { if (corners && !work && photo) placeHandles(); });

    for (const id of ['shGame', 'shSystem', 'shMat']) $(id).addEventListener('change', () => {
        S.game = $('shGame').value; S.shotSys = $('shSystem').value; S.shotMat = $('shMat').value;
        const g = GAME.find((x) => x.id === S.game);
        if (id === 'shGame' && g) { S.sdist = g.dist; $('shDist').value = String(g.dist); }
        if (id === 'shMat') { S.sv0 = S.shotMat === 'steel' ? 430 : 400; $('shV0').value = String(S.sv0); }
        fillShotNumbers(); renderShot();
    });
    for (const [id, key] of [['shDist', 'sdist'], ['shLoad', 'sload'], ['shV0', 'sv0']]) $(id).addEventListener('input', () => { const v = num($(id)); if (v > 0) { S[key] = v; renderShot(); } });
}

try {
    boot();
} catch (e) {
    window.__handleError?.(e, 'targets_boot', { say: t('load_error') });
}
